<template>
    <v-container fluid>
        <v-toolbar class="elevation-1">
            <v-toolbar-title>
                {{ $t("message.ListUsersTitle") }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
                v-if="$can('create', 'users')"
                @click="newItem()"
                color="primary"
                class="ml-4"
            >
                {{ $t("message.registerButton") }}
            </v-btn>
            <v-dialog
                @keydown.esc="close()"
                v-model="dialog"
                max-width="800"
                persistent
                scrollable
                :transition="
                    $vuetify.breakpoint.smAndDown
                        ? 'slide-y-transition'
                        : 'scale-transition'
                "
                :fullscreen="$vuetify.breakpoint.smAndDown"
            >
                <v-card class="register-card" justify-space-between>
                    <v-card-title>
                        <span class="headline">{{ formTitle }}</span>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="close()">
                            <v-icon medium>cancel</v-icon>
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <v-form ref="form">
                            <v-container>
                                <v-row dense>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            spellcheck="false"
                                            outlined
                                            dense
                                            v-model="user.name"
                                            :rules="[
                                                rules.required,
                                                rules.max44Chars,
                                                rules.nameAllowedCharacters,
                                            ]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[0].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            spellcheck="false"
                                            outlined
                                            dense
                                            v-model="user.phone"
                                            :rules="[
                                                rules.required,
                                                rules.max19Chars,
                                            ]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[3].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-text-field>
                                    </v-col>

                                    <!-- Company -->
                                    <v-col cols="12" md="6">
                                        <v-select
                                            outlined
                                            dense
                                            v-model="user.company_id"
                                            :items="companies"
                                            item-text="name"
                                            item-value="id"
                                            :rules="[rules.required]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[1].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            outlined
                                            dense
                                            spellcheck="false"
                                            v-model="user.email"
                                            :rules="[
                                                rules.required,
                                                rules.max59Chars,
                                                rules.email,
                                            ]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[2].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            outlined
                                            dense
                                            spellcheck="false"
                                            v-model="user.ldap_login"
                                            :rules="[
                                                rules.required,
                                                rules.max44Chars,
                                                rules.ldapLoginAllowedCharacters,
                                            ]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[4].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-select
                                            outlined
                                            dense
                                            v-model="user.group"
                                            :items="[...groups].sort()"
                                            :rules="[rules.required]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[5].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-select>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-form>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn class="list-button" @click="save">
                            <v-icon
                                class="pr-2"
                                color="success"
                                style="vertical-align: middle"
                            >
                                save
                            </v-icon>
                            {{ $t("message.SaveButton") }}
                        </v-btn>
                        <v-btn class="list-button" @click="close">
                            <v-icon
                                class="pr-2"
                                color="error"
                                style="vertical-align: middle"
                            >
                                cancel
                            </v-icon>
                            {{ $t("message.CancelButton") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-data-table
            :headers="headers"
            :loading="$store.getters.getLoading"
            :items="$store.getters.getUsers"
            :search.sync="$store.getters.getSearch"
            :lang="this.$i18n.locale"
            class="elevation-5"
            :items-per-page="35"
            height="calc(100vh - 220px - 40px)"
            sort-by="name"
            fixed-header
            mobile-breakpoint="0"
            :footer-props="$store.getters.getFooterProps"
        >
            <template slot="item" slot-scope="props">
                <tr
                    :style="$can('update', 'users') ? 'cursor:pointer' : ''"
                    @click="$can('update', 'users') ? editItem(props.item) : ''"
                    class="text-no-wrap"
                >
                    <td>{{ props.item.name }}</td>
                    <td>{{ props.item.company_name }}</td>
                    <td>{{ props.item.email }}</td>
                    <td>{{ props.item.phone }}</td>
                    <td>{{ props.item.ldap_login }}</td>
                    <td>{{ props.item.group }}</td>

                    <td v-if="$can('delete', 'users')">
                        <v-btn
                            class="caption font-weight-black"
                            small
                            @click.stop="deleteItem(props.item)"
                        >
                            <v-icon class="pr-2" color="error">delete</v-icon>
                            {{ $t("message.DeleteButton") }}
                        </v-btn>
                    </td>
                </tr>
            </template>
            <v-alert
                slot="no-results"
                dense
                :value="true"
                color="error"
                class="body-1 my-3"
                icon="warning"
            >
                {{ $t("message.YourSearch") }} "{{ $store.getters.getSearch }}"
                {{ $t("message.NoResults") }}
            </v-alert>
        </v-data-table>
    </v-container>
</template>

<script>
export default {
    data: () => ({
        setting: {
            rowsPerPage: -1,
        },
        dialog: false,
        groups: ["ADM", "WAR", "BAC", "HUM", "LIS", "FT2", "DRI", "PER"],
        user: {
            name: "",
            company_id: "",
            email: "",
            phone: "",
            ldap_login: "",
            group: "",
        },
        defaultUser: {
            name: "",
            company_id: "",
            email: "",
            phone: "",
            ldap_login: "",
            group: "",
        },
        editedIndex: -1,
        changed: false,
    }),
    mounted() {
        this.$store.dispatch("processUsers");
        this.$store.dispatch("processCompanies");
    },
    computed: {
        companies() {
            return [...this.$store.getters.getCompanies].sort((a, b) => {
                if (a.name < b.name) return -1;
                return Number(a.name > b.name);
            });
        },
        rules() {
            return {
                required: value => !!value || this.$t("message.FieldRequired"),
                email: value => {
                    const pattern =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return (
                        pattern.test(value) || this.$t("message.InvalidEmail")
                    );
                },
                nameAllowedCharacters: value => {
                    const error = this.$t("message.SpecialCharacters");
                    return /^([a-zA-Z0-9 _-]+)$/.test(value) || error;
                },
                ldapLoginAllowedCharacters: value => {
                    const error = this.$t("message.SpecialCharacters");
                    return /^([a-zA-Z0-9._-]+)$/.test(value) || error;
                },
                max19Chars: value => {
                    const error = this.$t("message.Max19Chars");
                    return !value || value.length <= 19 || error;
                },
                max44Chars: value => {
                    const error = this.$t("message.Max44Chars");
                    return !value || value.length <= 44 || error;
                },
                max59Chars: value => {
                    const error = this.$t("message.Max59Chars");
                    return !value || value.length <= 59 || error;
                },
            };
        },
        formTitle() {
            return this.editedIndex === -1
                ? this.$t("message.NewUser")
                : this.$t("message.EditUser");
        },
        headers() {
            return [
                {
                    text: this.$t("message.Name"),
                    value: "name",
                },
                {
                    text: this.$t("message.Company"),
                    value: "company_name",
                },
                {
                    text: this.$t("message.Email"),
                    value: "email",
                    sortable: true,
                },
                {
                    text: this.$t("message.Phone"),
                    value: "phone",
                    sortable: true,
                },
                {
                    text: this.$t("message.Ldap"),
                    value: "ldap_login",
                    sortable: true,
                },
                {
                    text: this.$t("message.Group"),
                    value: "group",
                    sortable: true,
                },
                {
                    text: this.$t("message.Actions"),
                    value: "actions",
                    sortable: false,
                    hidden: !this.$can("delete", "users"),
                },
            ].filter(title => !title.hidden);
        },
        isFilled() {
            return (
                !!this.user.name &&
                !!this.user.company_id &&
                !!this.user.email &&
                !!this.user.phone &&
                !!this.user.ldap_login &&
                !!this.user.group
            );
        },
        isValid() {
            return !(
                this.rules.email(this.user.email) ===
                    this.$t("message.InvalidEmail") ||
                this.rules.nameAllowedCharacters(this.user.name) ===
                    this.$t("message.SpecialCharacters") ||
                this.rules.ldapLoginAllowedCharacters(this.user.ldap_login) ===
                    this.$t("message.SpecialCharacters") ||
                this.rules.max19Chars(this.user.phone) ===
                    this.$t("message.Max19Chars") ||
                this.rules.max44Chars(this.user.name) ===
                    this.$t("message.Max44Chars") ||
                this.rules.max44Chars(this.user.ldap_login) ===
                    this.$t("message.Max44Chars") ||
                this.rules.max59Chars(this.user.email) ===
                    this.$t("message.Max59Chars")
            );
        },
    },

    watch: {
        user: {
            handler() {
                this.changed = true;
            },
            deep: true,
        },
        dialog(val) {
            if (val) {
                this.changed = false;
                this.setFocus();
            }
        },
    },
    methods: {
        setFocus() {
            setTimeout(() => {
                this.$refs.form.$children[0].focus();
                this.$refs.form.resetValidation();
            }, 0);
        },
        editItem(item) {
            this.editedIndex = this.$store.getters.getUsers.indexOf(item);
            Object.assign(this.user, this.defaultUser, item);
            this.dialog = true;
        },
        deleteItem(item) {
            this.$store.commit("popUp", {
                type: "deleteUser",
                id: item.id,
            });
        },
        newItem() {
            this.user = Object.assign({}, this.defaultUser);
            this.editedIndex = -1;
            this.dialog = true;
        },
        close() {
            if (
                this.changed &&
                // TODO: USE Alert component instaed
                // eslint-disable-next-line no-restricted-globals, no-alert
                !confirm("Are you sure ? Any unsaved changes will be lost.")
            ) {
                return;
            }

            this.dialog = false;
        },

        save() {
            if (!this.isFilled || !this.isValid) {
                this.$refs.form.validate();
                return;
            }

            if (this.editedIndex > -1) {
                this.$store.dispatch("editUser", this.user);
            } else {
                this.$store.dispatch("addUser", this.user);
            }

            this.dialog = false;
        },
    },
};
</script>
